import type { TimePeriod } from '../types'

export const TIME_PERIOD_DAYS: Record<TimePeriod, number> = {
  day: 1,
  week: 7,
  month: 30,
  year: 365,
}

/**
 * Calculates the number of buffer days for a given lookback period in days.
 * Rules:
 * - 0-3 days: 0 buffer days
 * - 4-6 days: 1 buffer day
 * - >= 7 days: floor(days / 7) + 1, maxing out at 10 buffer days
 */
export const getBufferDays = (days: number): number => {
  if (days <= 3) return 0
  if (days <= 6) return 1
  return Math.min(10, Math.floor(days / 7) + 1)
}

/**
 * Applies buffer day adjustment to a lookback period.
 *
 * @param value The lookback period value
 * @param uom The unit of measure
 * @returns The adjusted days and the original days
 */
export const applyBufferDayAdjustment = (
  value: number | undefined | null,
  uom: TimePeriod | undefined | null,
  bufferIntervals = true,
): { adjustedDays?: number; originalDays?: number; bufferDays?: number } => {
  if (value === undefined || value === null || !uom) {
    return {}
  }

  const multiplier = TIME_PERIOD_DAYS[uom]
  if (!multiplier) {
    return {}
  }

  const originalDays = value * multiplier
  const bufferDays = bufferIntervals ? getBufferDays(originalDays) : 0
  const adjustedDays = Math.max(1, originalDays - bufferDays)

  return {
    adjustedDays,
    originalDays,
    bufferDays,
  }
}
